import { AppBar, Box, IconButton, Tabs, Toolbar, styled } from '@mui/material';
import { useLocation } from 'react-router';

import { SIZES, useMenuItems } from '../../../../assets/constants/constants';
import CloseXIcon from '../../../../assets/images/close_x.svg?styled';
import HamburgerMenuIcon from '../../../../assets/images/hamburger_menu.svg?styled';
import Logo from '../../../../assets/images/logo.svg';
import useIsMobile from '../../../../hooks/IsMobile';
import useIsMyScoopr from '../../../../hooks/IsMyScoopr';
import { LANG } from '../../../../services/Config';
import MenuActionButton from '../MenuActionButton';

import TopBarItem from './_components/TopBarItem/index';

import { PLink } from '@/ui/PLink';

// Styled Components
const StyledLogo = styled('img')(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  height: 18,
  width: 109,
  [theme.breakpoints.up('md')]: {
    height: 25,
  },
}));

const getActiveTab = (path: string, items: any[]) => {
  return items.find((i) => path.startsWith(i.href))?.href || false;
};

interface TopBarProps {
  setSideBarOpen?(...args: unknown[]): unknown;
  isSideBarOpen?: boolean;
}

const TopBar = ({
  setSideBarOpen = () => {},
  isSideBarOpen = false,
  ...rest
}: TopBarProps) => {
  const isMyScoopr = useIsMyScoopr();
  const { isMobile } = useIsMobile();

  const { appStrings, routes } = LANG;
  const { pathname } = useLocation();

  const menuItems = useMenuItems(appStrings, routes);
  const activeTab = getActiveTab(pathname, menuItems);

  const { search } = useLocation();

  return (
    <AppBar
      position="sticky"
      elevation={20}
      sx={(theme) => ({
        alignItems: 'center',
        padding: {
          xs: theme.spacing(0, 2),
          md: theme.spacing(0, 4),
        },
        ...(isMyScoopr &&
          isMobile && {
            [theme.breakpoints.down('sm')]: {
              background: theme.palette.background.default,
            },
          }),
      })}
      {...rest}
    >
      <Toolbar
        role="toolbar"
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          height: SIZES.topBar.height.small,
          minHeight: SIZES.topBar.height.small,
          width: '100%',
          maxWidth: SIZES.layout.width.xl,
          padding: 0,
          [theme.breakpoints.up(SIZES.navigationChange)]: {
            justifyContent: 'space-between',
            height: SIZES.topBar.height.large,
            minHeight: SIZES.topBar.height.large,
          },
        })}
      >
        <IconButton
          color="primary"
          onClick={setSideBarOpen}
          aria-label={
            isSideBarOpen ? appStrings.MENU.CLOSE : appStrings.MENU.OPEN
          }
          size="large"
          sx={(theme) => ({
            position: 'absolute',
            left: theme.spacing(1),
            '& svg': {
              fontSize: '1.875rem',
            },
            [theme.breakpoints.up(SIZES.navigationChange)]: {
              display: 'none',
            },
          })}
        >
          {isSideBarOpen ? (
            <CloseXIcon sx={{ color: '#18A0FB' }} />
          ) : (
            <HamburgerMenuIcon />
          )}
        </IconButton>

        <PLink aria-label={appStrings.MENU.HOME} to={`${routes.HOME}${search}`}>
          <StyledLogo alt="Scoopr logo" src={Logo} />
        </PLink>

        <Box
          sx={(theme) => ({
            display: 'none',
            [theme.breakpoints.up(SIZES.navigationChange)]: {
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            },
          })}
        >
          <Tabs
            indicatorColor="primary"
            textColor="primary"
            value={activeTab}
            sx={{
              height: SIZES.topBar.height.large,
            }}
          >
            {menuItems.map((item) => (
              <TopBarItem key={item.href} value={item.href} menuItem={item} />
            ))}
          </Tabs>
          <Box sx={(theme) => ({ marginLeft: theme.spacing(3) })}>
            <MenuActionButton />
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
